import { ChangeDetectorRef, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TransactionPrivate } from '@app/core/resources/transaction-private';
import { User } from '@app/core/resources/user';
import { MetasService } from '@app/core/services/metas.service';
import { TransactionPrivateService } from '@app/core/services/transaction-private.service';
import { UsersService } from '@app/core/services/users.service';
import { Observable, of, Subject } from 'rxjs';
import { filter, flatMap } from 'rxjs/operators';
import { MessageAlertQuotationHelper } from '@app/transactions/helper/message-alert-quotation.helper';
import { AccountService } from '@app/core/services/account.service';
import { DocumentCollection } from 'ngx-jsonapi';
import { Account } from '@app/core/resources/account';
import { AlertAccountComponent } from '../accounts/account-autocomplete/alert-account/alert-account.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NotifyService } from '@app/exceptions/services/notify.service';

@Injectable({
    providedIn: 'root'
})
export class TransactionsService {
    private transaction: TransactionPrivate = new TransactionPrivate();
    private user: User = new User();
    private changeDetectorRef!: ChangeDetectorRef;
    private systemWay: number = 2;
    private address: string = '';
    public transactionSave$: Subject<any> = new Subject();
    public form!: FormGroup;
    public user$: Subject<any> = new Subject();
    public isSaving: boolean = false;
    public havePhone: boolean = false;
    public userId: string | undefined = '';
    private method!: (props?: any) => void;

    public constructor(
        private usersService: UsersService,
        private transactionPrivateService: TransactionPrivateService,
        public messageAlertQuotationHelper: MessageAlertQuotationHelper,
        private matDialog: MatDialog,
        private accountsService: AccountService,
        private notifyService: NotifyService,
        public metasService: MetasService
    ) {
        this.transactionPrivateService.register();
        this.getUser();
        this.messageAlertQuotationHelper.transaction = this.transaction;
        this.transactionSave$.subscribe(({ transaction, form }) => {
            this.transaction = transaction;
            this.form = form;
            this.save();
        });
    }

    public setMethod(method: any): void {
        this.method = method;
    }

    public setChangeDetectorRef(changeDetectorRef: ChangeDetectorRef): void {
        this.changeDetectorRef = changeDetectorRef;
    }

    private getUser(): void {
        this.user$.subscribe((userId: string) => {
            this.usersService.get(userId).subscribe((user) => {
                this.user = user;
                this.userId = userId;
                if (user.attributes.phone_number) {
                    this.havePhone = true;
                }
                if (this.changeDetectorRef) {
                    this.changeDetectorRef.detectChanges();
                }
            });
        });
    }

    public save(): void {
        of(true)
            .pipe(
                flatMap((): Observable<any> => {
                    if (this.havePhone) {
                        return of(true);
                    }

                    this.user.attributes.phone_number = this.form.controls.whatsapp.value;

                    return this.user.save();
                })
            )
            .subscribe(
                () => {
                    this.saveTransaction();
                },
                (error) => {
                    this.isSaving = false;
                    throw error;
                }
            );
    }

    private updateStateAccount(address: string, systemId: string): void {
        if (address === '' || systemId === '') {
            this.notifyService.error('Error al actualizar la cuenta');

            return;
        }
        this.accountsService
            .all({
                beforepath: `users/${this.userId}`,
                remotefilter: { address: address, 'system.id': systemId },
                include: ['system', 'account_detail']
            })
            .pipe(filter((account: DocumentCollection<Account>): boolean => account.source !== 'new'))
            .subscribe((accounts: DocumentCollection<Account>) => {
                let account: Account = accounts.data[0];
                if (!account) {
                    return;
                }
                account.attributes.status = 'normal';
                account.save({ beforepath: 'users/' + this.userId }).subscribe(() => {
                    this.transaction.relationships['account' + this.systemWay].data = account;
                    this.saveTransaction();
                });
            });
    }

    private getAccountDirectionByError(error: string): void {
        // error message -> La cuenta <address_account> para <system_name> está archivada.
        let errorArray: Array<string> = error.split(' ');
        if (errorArray.length === 0) {
            return;
        }

        this.systemWay = error.includes(this.transaction.relationships.system1.data?.attributes.name ?? '') ? 1 : 2;
        this.address = errorArray[2];
    }

    private saveTransaction(): void {
        let transaction: TransactionPrivate = new TransactionPrivate();
        transaction.attributes = (this.transaction as TransactionPrivate).attributes;
        transaction.relationships = (this.transaction as TransactionPrivate).relationships;
        let include: Array<string> = ['metas'];
        if (transaction.relationships.account1.data?.id === 'new_account_1') {
            include.push('account1');
        }
        if (transaction.relationships.account2.data?.id === 'new_account_2') {
            include.push('account2');
        }
        transaction.save({ beforepath: 'users/' + this.userId, include: include }).subscribe(
            (): void => {
                this.isSaving = false;
                this.method(transaction);
                this.changeDetectorRef.detectChanges();
            },
            (error): void => {
                this.isSaving = false;

                if (error.errors[0].code.includes('account_is_archived')) {
                    this.openAlertAccount(transaction);
                    this.getAccountDirectionByError(error.errors[0].detail);

                    return;
                }
                this.changeDetectorRef.detectChanges();
                throw error;
            }
        );
    }

    private openAlertAccount(transaction: TransactionPrivate): void {
        let dialog: MatDialogRef<AlertAccountComponent> = this.matDialog.open(AlertAccountComponent, {
            width: '600px',
            autoFocus: false
        });
        dialog.afterClosed().subscribe((data): void => {
            if (data) {
                this.updateStateAccount(this.address, transaction.relationships['system' + this.systemWay].data.id);
            }
            this.changeDetectorRef.detectChanges();

            return;
        });
    }
}
